import './DemoRipButton.css';

function PlayGlyph({ compact }: { compact: boolean }) {
  return (
    <span
      aria-hidden
      className={
        'relative inline-flex shrink-0 items-center justify-center rounded-full ' +
        (compact ? 'h-7 w-7' : 'h-9 w-9')
      }
      style={{
        background: 'var(--co-DemoRipButton-iconDisc, #FFFFFF)',
        // The ring rides on `currentColor`, so it always matches the disc.
        color: 'var(--co-DemoRipButton-iconDisc, #FFFFFF)',
      }}
    >
      <span className="demo-rip-wave absolute inset-0 rounded-full" />
      <svg
        width={compact ? 9 : 12}
        height={compact ? 11 : 14}
        viewBox="0 0 12 14"
        fill="none"
        className="demo-rip-pulse relative"
        style={{
          marginLeft: compact ? 1.5 : 2,
          color: 'var(--co-DemoRipButton-icon, var(--ui-accent))',
        }}
      >
        <path d="M0.5 0.87L11 7L0.5 13.13V0.87Z" fill="currentColor" />
      </svg>
    </span>
  );
}

export interface DemoRipButtonProps {
  onClick: () => void;
  /** Stretches to the width of its container — used in the desktop
   *  sidebar between OddsPanel and BottomAction. The mobile header pill
   *  keeps its natural (content) width. */
  fullWidth?: boolean;
  className?: string;
}

/**
 * Entry point for the free, no-registration demo rip (user reference,
 * 2026-09-22): a bright accent pill, a white play disc that breathes at
 * the left edge, and the label centred across the pill.
 *
 * Nothing is hardcoded green — both gradient stops default off
 * `--ui-accent`, so a re-skinned operator gets their own accent — and every
 * layer is overridable from the Design Constructor (`DemoRipButton` in the
 * registry; the invariant spec in packages/theme-constructor gates that the
 * two stay in sync).
 */
export function DemoRipButton({ onClick, fullWidth = false, className = '' }: DemoRipButtonProps) {
  // The mobile pill sits in the header row next to the category dropdown, so
  // it matches that pill's height (h-9) and 13px label instead of towering
  // over it; the sidebar's full-width variant keeps the taller CTA size.
  const compact = !fullWidth;
  return (
    <button
      type="button"
      onClick={onClick}
      data-co-component="DemoRipButton"
      className={
        'relative isolate flex items-center cursor-pointer select-none ' +
        'transition-[filter,transform] duration-150 ' +
        'hover:[filter:brightness(1.04)] active:scale-[0.99] ' +
        (compact ? 'h-9 pl-1 pr-3.5 gap-2' : 'h-12 pl-1.5 pr-4 w-full') +
        ' ' +
        className
      }
      style={{
        background:
          'linear-gradient(90deg, var(--co-DemoRipButton-fillFrom, var(--ui-accent)) 0%, var(--co-DemoRipButton-fillTo, color-mix(in srgb, var(--co-DemoRipButton-fillFrom, var(--ui-accent)) 58%, white 42%)) 100%)',
        color: 'var(--co-DemoRipButton-text, #FFFFFF)',
        borderRadius: 'var(--co-DemoRipButton-radius, 999px)',
        boxShadow:
          'var(--co-DemoRipButton-shadow, 0 8px 20px 0 color-mix(in srgb, var(--ui-accent) 40%, transparent))',
      }}
    >
      {/* Diagonal gloss band — the sheen across the reference pill. Purely
          decorative, sits under the content (`-z-10` against the button's
          own stacking context) so it never eats a tap. */}
      {/* The gloss needs clipping to the pill; the button itself must NOT
          clip, or the play disc's expanding wave gets sliced into a bar. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
        style={{ borderRadius: 'var(--co-DemoRipButton-radius, 999px)' }}
      >
        <span
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(104deg, rgba(255,255,255,0) 18%, rgba(255,255,255,0.22) 34%, rgba(255,255,255,0) 52%)',
          }}
        />
      </span>
      <PlayGlyph compact={compact} />
      {/* Full width: centred across the whole pill (not just the space left
          of the disc), so the label sits on the pill's axis like the
          reference. Compact: right after the disc. */}
      <span
        className={
          'whitespace-nowrap ' +
          (compact
            ? 'text-[13px] font-semibold tracking-[-0.1px]'
            : 'type-subhead font-bold absolute inset-x-0 text-center pointer-events-none')
        }
      >
        Try a free demo rip
      </span>
    </button>
  );
}
